import React, {useEffect, useMemo, useState} from "react";
import {translate} from "../../types/EnumTranslations";
import {BaseType} from "../../types/Enums";
import InfoPanel, {InfoItem} from "../../Components/InfoPanel";
import {AREA_ESTIMATE_INFO, FLOOR_AREA_NO_FLOOR_HINT, floorAreaStaleNote, floorAreaVerifiedInfo} from "../../constants/infoTexts";
import VerifiedAreaCheck from "../../Components/VerifiedAreaCheck";
import RoomFloorAreaModal, {FloorAreaRow} from "./RoomFloorAreaModal";
import {roomFloorAreaApi, SaveRoomFloorAreaInput} from "../../api/roomFloorAreaApi";
import {getFloorAreaVerificationState, verificationDate} from "../../Helpers/floorAreaVerification";
import {useUserNames} from "../../Helpers/useUserNames";
import {useBuilding} from "../../Helpers/BuildingContext";
import {getMaterialTranslation} from "../../contexts/MaterialsContext";
import {useDocumentTitle} from "../../hooks/useDocumentTitle";
import PhotoGallery from "../../Components/PhotoGallery";
import {resolveFirebaseImageUrl} from "../../Helpers/ImageLoader";
import {BaseComponentImpl} from "../../types/implementations";
import {PhotoModel} from "../../types/protocols";
import {BreadcrumbComponent} from "../../Components/BreadCrumb";
import { useUnits } from "../../hooks/units";
import { useFeature } from "../../hooks/useFeatures";
import { unitDisplayName, unitHoldingRoom } from "../../Helpers/unitUtils";
import { isMultiLevelRoom, roomLevelSpanLabel } from "../../Helpers/roomUtils";
import { Unit } from "../../types/units";
import { Link } from "react-router-dom";
import NestedRiskConstruction from "../../Components/NestedRiskConstruction";
import NestedHazardView from "../../Components/NestedHazardView";
import {useRoom} from "../../hooks/room";
import RoomBlueprintSection from "./RoomBlueprintSection";
import RoomComponentDetailsPane, {RightPaneContent} from "./RoomComponentDetailsPane";
import ComponentButtonList from "./ComponentButtonList";
import ExpandableComponentSection from "./ExpandableComponentSection";
import WorkEnvironmentRiskSection from "./WorkEnvironmentRiskSection";
import RoomHistorySection from "./RoomHistorySection";
import EntityActionsDropdown from "../../Components/EntityActionsDropdown";
import { PageLoader } from "../../Components/LoadingSpinner";
import { orderPhotos } from "../../Helpers/photoOrdering";


type PaneSelection =
    | Exclude<RightPaneContent, { type: 'component' } | null>
    | { type: 'component'; componentKey: string }
    | null;

const RoomPage: React.FC = () => {
    const {
        buildingKey, roomKey, room, level,
        currentBuilding, loadingBuilding, buildingError,
        currentBlueprint, roomBlueprintLocation, otherLevelDrawings,
        utilities, fixedFurnishings, membranes, windows, doors,
        ceilingComponents, wallComponents, floorComponents,
        totalCeilingArea, totalWallArea, totalFloorArea,
        totalCeilingAreaExcludingOpenings, totalWallAreaExcludingOpenings, totalFloorAreaExcludingOpenings,
        riskConstructions, hazards,
    } = useRoom();
    // Which unit (lägenhet/lokal) holds this room, from the loaded building (plan D6: no reverse pointer).
    const { sortedUnits } = useUnits({ buildingKey });
    // No "Tillhör" badge while Lägenheter & lokaler is off
    const unitsEnabled = useFeature('units');
    const { fetchBuilding } = useBuilding();

    // Golvyta uppmätt på ritning (web#1383): derived from the room's
    // verification and the floors' areaShapes timestamps, never stored.
    const floorAreaState = useMemo(
        () => getFloorAreaVerificationState(room?.floorAreaVerification, floorComponents),
        [room?.floorAreaVerification, floorComponents]
    );
    const { getUserName } = useUserNames();
    const [verifierName, setVerifierName] = useState<string>('');
    const verifiedBy = floorAreaState.kind === 'none' ? undefined : floorAreaState.verification.verifiedBy;
    useEffect(() => {
        if (!verifiedBy) return;
        let cancelled = false;
        getUserName(verifiedBy).then(name => { if (!cancelled) setVerifierName(name); });
        return () => { cancelled = true; };
    }, [verifiedBy, getUserName]);
    const [showFloorAreaModal, setShowFloorAreaModal] = useState(false);

    const floorAreaRows = useMemo((): FloorAreaRow[] => floorComponents.map((floor, index) => {
        const materials = floor.material?.value?.map(m => getMaterialTranslation(m)).filter(Boolean) ?? [];
        const name = floor.label?.value || (materials.length > 0 ? materials.join(', ') : `Golv ${index + 1}`);
        return {
            componentKey: floor.key,
            name,
            currentArea: currentBuilding ? floor.totalArea(currentBuilding) : null,
        };
    }), [floorComponents, currentBuilding]);

    const handleSaveFloorArea = async (input: SaveRoomFloorAreaInput) => {
        if (!buildingKey || !roomKey) return;
        await roomFloorAreaApi.save(buildingKey, roomKey, input);
        await fetchBuilding(buildingKey);
    };

    // Set document title based on state
    useDocumentTitle(
        loadingBuilding ? 'Laddar rum...' :
        buildingError ? 'Fel - Rum' :
        !room ? 'Rum' :
        room.displayName()
    );

    const [hoveredComponentKey, setHoveredComponentKey] = useState<string | null>(null);
    // A selected component is held by key and read from the building on every
    // render, so edits made in the right pane show there
    const [paneSelection, setPaneSelection] = useState<PaneSelection>(null);
    const rightPaneContent = useMemo<RightPaneContent>(() => {
        if (paneSelection?.type !== 'component') return paneSelection;
        const data = currentBuilding?.model.structure?.components?.[paneSelection.componentKey];
        return data ? { type: 'component', component: new BaseComponentImpl(data) } : null;
    }, [paneSelection, currentBuilding]);

    const selectedComponentKey = paneSelection?.type === 'component' ? paneSelection.componentKey : null;

    // Arrow-key navigation between sibling photos while a photo is shown in the
    // right pane (works for both Rumsfoton and work-environment-risk photos,
    // whichever set the pane was opened from).
    useEffect(() => {
        if (rightPaneContent?.type !== 'photo') return;
        const content = rightPaneContent;
        const handleKeyDown = (e: KeyboardEvent) => {
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
            e.preventDefault();
            const entries = content.allPhotos;
            if (entries.length < 2) return;
            const idx = entries.findIndex(p => p.key === content.photo.key);
            if (idx < 0) return;
            const direction = e.key === 'ArrowLeft' ? -1 : 1;
            const next = entries[(idx + direction + entries.length) % entries.length];
            // Resolve the real URL first so the preview swaps in one step
            // (no placeholder flash), then patch the pane.
            resolveFirebaseImageUrl(next.storagePath)
                .catch(() => next.storagePath)
                .then(url => setPaneSelection(cur =>
                    cur?.type === 'photo' ? { ...cur, photo: next, url } : cur));
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [rightPaneContent]);

    const handleComponentMouseEnter = (key: string) => setHoveredComponentKey(key);
    const handleComponentMouseLeave = () => setHoveredComponentKey(null);
    const handleComponentClick = (componentKey: string) => {
        if (currentBuilding?.model.structure?.components?.[componentKey]) {
            setPaneSelection({ type: 'component', componentKey });
        }
    };

    const handleRiskPhotoClick = (photo: PhotoModel, url: string, allPhotos: { [key: string]: PhotoModel }) => {
        if (buildingKey) {
            setPaneSelection({ type: 'photo', photo, url, allPhotos: orderPhotos(allPhotos), buildingKey, title: 'Arbetsmiljörisk — Foto' });
        }
    };

    const handleRiskDocumentClick = (url: string, fileName: string, mimeType: string) => {
        setPaneSelection({ type: 'document', url, fileName, mimeType });
    };

    const handleRoomPhotoClick = (photo: PhotoModel, url: string, _index: number) => {
        if (buildingKey && room?.photos) {
            setPaneSelection({ type: 'photo', photo, url, allPhotos: orderPhotos(room.photos), buildingKey, title: 'Rumsfoto' });
        }
    };

    const markerComponents = useMemo(() =>
        [...utilities, ...fixedFurnishings, ...membranes, ...windows, ...doors],
        [utilities, fixedFurnishings, membranes, windows, doors]
    );

    const hasAnyComponents = utilities.length > 0 || fixedFurnishings.length > 0 || membranes.length > 0 || windows.length > 0 || doors.length > 0 ||
        ceilingComponents.length > 0 || wallComponents.length > 0 || floorComponents.length > 0;

    if (loadingBuilding) return <PageLoader />;
    if (buildingError) return <p>{buildingError}</p>;
    if (!currentBuilding) return <p>No building data available</p>;
    if (!room) return <p>No room data available</p>;
    if (!buildingKey) return <>Building key missing</>;
    if (!roomKey) return <>Room key missing</>;
    const holdingUnit = unitsEnabled ? unitHoldingRoom(sortedUnits, roomKey) : undefined;

    // Helper to format area with optional "inkl öppningar" when values differ
    const formatAreaWithOpenings = (total: number, excluding: number, label: string) => {
        if (total <= 0) return { label, value: "Okänd kvm", info: AREA_ESTIMATE_INFO };
        const diff = Math.abs(total - excluding);
        if (diff > 0.01) {
            return { label, value: `${excluding.toFixed(1)} kvm (${total.toFixed(1)} kvm inkl öppningar)`, info: AREA_ESTIMATE_INFO };
        }
        return { label, value: `${total.toFixed(1)} kvm`, info: AREA_ESTIMATE_INFO };
    };

    // The Golvyta row: pencil for everyone (disabled without a floor), and the
    // verification state replacing or extending the estimate disclaimer.
    const golvytaItem = (): InfoItem => {
        const base = formatAreaWithOpenings(totalFloorArea, totalFloorAreaExcludingOpenings, 'Golvyta');
        const hasFloor = floorComponents.length > 0;
        const item: InfoItem = {
            ...base,
            onEdit: () => setShowFloorAreaModal(true),
            editDisabled: !hasFloor,
            editTitle: hasFloor ? 'Ändra golvyta' : FLOOR_AREA_NO_FLOOR_HINT,
        };
        if (floorAreaState.kind === 'verified') {
            item.info = floorAreaVerifiedInfo(verificationDate(floorAreaState.verification), verifierName || 'Okänd användare', floorAreaState.verification.comment);
            item.valueIcon = <VerifiedAreaCheck />;
        } else if (floorAreaState.kind === 'stale') {
            item.info = `${AREA_ESTIMATE_INFO}\n${floorAreaStaleNote(verificationDate(floorAreaState.verification))}`;
        }
        return item;
    };

    const breadCrumbItems = level
        ? [
            { text: currentBuilding.summary.name ?? 'Byggnad', link: `/building/${buildingKey}` },
            { text: level.displayName(), link: `/building/${buildingKey}/level/${level.key}` },
            { text: room.displayName() }
        ]
        : [
            { text: currentBuilding.summary.name ?? 'Byggnad', link: `/building/${buildingKey}` },
            { text: room.displayName() }
        ];

    // Highlight the selected thumbnail in whichever gallery the pane photo came from
    const selectedPanePhotoKey = rightPaneContent?.type === 'photo' ? rightPaneContent.photo.key : undefined;
    const selectedRoomPhotoKey = selectedPanePhotoKey && room.photos?.[selectedPanePhotoKey] ? selectedPanePhotoKey : undefined;
    const selectedRiskPhotoKey = selectedPanePhotoKey && room.workEnvironmentRisk?.photos?.[selectedPanePhotoKey] ? selectedPanePhotoKey : undefined;

    return (
        <>
            <BreadcrumbComponent items={breadCrumbItems} />

            <div style={{ padding: "20px", maxWidth: '100%'}}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <h3 style={{margin: 0}}>{room.displayName()}</h3>
                        {holdingUnit && (
                            <Link to={`/building/${buildingKey}/units${room.levelKey ? `?level=${room.levelKey}` : ''}`} className="badge rounded-pill text-bg-light" style={{ fontSize: '13px', textDecoration: 'none' }}>
                                Tillhör {unitDisplayName(holdingUnit as Unit)}
                            </Link>
                        )}
                    </div>
                    <EntityActionsDropdown
                        buildingKey={buildingKey}
                        entityKey={roomKey}
                        entityType="room"
                        size="large"
                    />
                </div>

                {/* Two-pane layout */}
                <div style={{ display: 'flex', gap: '40px', width: '100%' }}>
                    {/* Left Pane */}
                    <div style={{ width: '60%', maxWidth: '900px', minWidth: '400px' }}>
                        <div
                            className="std-box dropshadow"
                            style={{
                                width: '100%',
                                padding: '40px',
                                marginBottom: '20px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '40px',
                            }}>
                            <div style={{display: 'flex', gap: '40px'}}>
                                {/* Blueprint, risks, hazards, photos - 2/3 width */}
                                <div style={{ flex: '2', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    <RoomBlueprintSection
                                        currentBlueprint={currentBlueprint}
                                        currentBlueprintKey={level?.currentBlueprintKey}
                                        roomBlueprintLocation={roomBlueprintLocation}
                                        building={currentBuilding}
                                        room={room}
                                        markerComponents={markerComponents}
                                        selectedComponentKey={selectedComponentKey}
                                        onComponentClick={handleComponentClick}
                                    />
                                    {/* A room spanning several levels: one drawing per further level, lowest first; its utilities sit on the primary level only (multi-level rooms R16). */}
                                    {otherLevelDrawings.map(entry => (
                                        <RoomBlueprintSection
                                            key={entry.level.key}
                                            title={`Ritning · ${entry.level.displayName()}`}
                                            currentBlueprint={entry.blueprint}
                                            currentBlueprintKey={entry.level.currentBlueprintKey}
                                            roomBlueprintLocation={entry.location}
                                            building={currentBuilding}
                                            room={room}
                                            markerComponents={[]}
                                            selectedComponentKey={null}
                                            onComponentClick={handleComponentClick}
                                        />
                                    ))}

                                    {riskConstructions.length !== 0 && (
                                        <NestedRiskConstruction
                                            riskConstructions={riskConstructions}
                                            buildingKey={buildingKey}
                                        />
                                    )}

                                    {hazards.length !== 0 && (
                                        <NestedHazardView
                                            hazards={hazards}
                                            buildingKey={buildingKey}
                                        />
                                    )}

                                    <WorkEnvironmentRiskSection
                                        buildingKey={buildingKey}
                                        risk={room.workEnvironmentRisk}
                                        onPhotoClick={handleRiskPhotoClick}
                                        onDocumentClick={handleRiskDocumentClick}
                                        selectedPhotoKey={selectedRiskPhotoKey}
                                    />

                                    {room.photos && Object.keys(room.photos).length > 0 && (
                                        <div>
                                            <h4 style={{marginBottom: '20px'}}>Rumsfoton</h4>
                                            <PhotoGallery
                                                photos={room.photos}
                                                buildingKey={buildingKey}
                                                onImageClick={handleRoomPhotoClick}
                                                selectedPhotoKey={selectedRoomPhotoKey}
                                            />
                                        </div>
                                    )}

                                    <RoomHistorySection
                                        buildingKey={buildingKey}
                                        roomKey={roomKey}
                                    />
                                </div>

                                {/* Facts and component lists - 1/3 width */}
                                <div style={{ flex: '1', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    <div>
                                        <h4 style={{marginBottom: '20px'}}>Fakta</h4>
                                        <InfoPanel
                                            items={[
                                                {label: isMultiLevelRoom(room, currentBuilding.model.structure?.levels) ? 'Våningar' : 'Våning', value: isMultiLevelRoom(room, currentBuilding.model.structure?.levels) ? roomLevelSpanLabel(room, currentBuilding.model.structure?.levels) : level ? level.formattedNumber() : "-"},
                                                {label: 'Rumsnummer', value: room.number ?? "-"},
                                                {label: 'Rumstyp', value: translate(room.type)},
                                                {label: 'Eget namn', value: room.label ?? "-"},
                                                formatAreaWithOpenings(totalCeilingArea, totalCeilingAreaExcludingOpenings, 'Takyta'),
                                                formatAreaWithOpenings(totalWallArea, totalWallAreaExcludingOpenings, 'Väggyta'),
                                                golvytaItem(),
                                                ...(room.electricalInstallation?.value ? [
                                                    ...(room.electricalInstallation.value.powerOutletCount && room.electricalInstallation.value.powerOutletCount > 0 ?
                                                        [{label: 'Eluttag', value: room.electricalInstallation.value.powerOutletCount.toString()}] : []),
                                                    ...(room.electricalInstallation.value.lightSwitchCount && room.electricalInstallation.value.lightSwitchCount > 0 ?
                                                        [{label: 'Strömbrytare', value: room.electricalInstallation.value.lightSwitchCount.toString()}] : []),
                                                    ...(room.electricalInstallation.value.lampCount && room.electricalInstallation.value.lampCount > 0 ?
                                                        [{label: 'Belysning', value: room.electricalInstallation.value.lampCount.toString()}] : [])
                                                ] : []),
                                                ...(room.airTerminalInstallation?.value ? [
                                                    ...(room.airTerminalInstallation.value.exhaustCount && room.airTerminalInstallation.value.exhaustCount > 0 ?
                                                        [{label: 'Frånluft', value: room.airTerminalInstallation.value.exhaustCount.toString()}] : []),
                                                    ...(room.airTerminalInstallation.value.supplyCount && room.airTerminalInstallation.value.supplyCount > 0 ?
                                                        [{label: 'Tilluft', value: room.airTerminalInstallation.value.supplyCount.toString()}] : []),
                                                    ...(room.airTerminalInstallation.value.supplyAndExhaustCount && room.airTerminalInstallation.value.supplyAndExhaustCount > 0 ?
                                                        [{label: 'Från/till', value: room.airTerminalInstallation.value.supplyAndExhaustCount.toString()}] : [])
                                                ] : []),
                                                ...(room.radiatorInstallation?.value ? [
                                                    ...(room.radiatorInstallation.value.waterRadiatorCount && room.radiatorInstallation.value.waterRadiatorCount > 0 ?
                                                        [{label: 'Vattenradiatorer', value: room.radiatorInstallation.value.waterRadiatorCount.toString()}] : []),
                                                    ...(room.radiatorInstallation.value.electricRadiatorCount && room.radiatorInstallation.value.electricRadiatorCount > 0 ?
                                                        [{label: 'Elradiatorer', value: room.radiatorInstallation.value.electricRadiatorCount.toString()}] : [])
                                                ] : []),
                                                ...(room.hasPoorReception ? [{label: 'Mottagning', value: 'Dålig mottagning', valueColor: '#DC143C'}] : [])
                                            ]}
                                        />
                                    </div>

                                    <ExpandableComponentSection
                                        title="Fastighetsteknik"
                                        groups={[
                                            { components: utilities, defaultImagePath: "placeholder.png", getMetadata: (c) => [translate(c.type), ...(c.label?.value ? [c.label.value] : [])] },
                                            { components: membranes, defaultImagePath: "Basetypes/membrane.png" },
                                        ]}
                                        building={currentBuilding}
                                        selectedComponentKey={selectedComponentKey}
                                        hoveredKey={hoveredComponentKey}
                                        onComponentClick={handleComponentClick}
                                        onMouseEnter={handleComponentMouseEnter}
                                        onMouseLeave={handleComponentMouseLeave}
                                    />

                                    <ExpandableComponentSection
                                        title={translate(BaseType.FixedFurnishing)}
                                        groups={[{ components: fixedFurnishings, defaultImagePath: "Basetypes/fixed_furnishing.png" }]}
                                        building={currentBuilding}
                                        selectedComponentKey={selectedComponentKey}
                                        hoveredKey={hoveredComponentKey}
                                        onComponentClick={handleComponentClick}
                                        onMouseEnter={handleComponentMouseEnter}
                                        onMouseLeave={handleComponentMouseLeave}
                                    />

                                    <ExpandableComponentSection
                                        title="Fönster"
                                        groups={[{ components: windows, defaultImagePath: "placeholder.png" }]}
                                        building={currentBuilding}
                                        selectedComponentKey={selectedComponentKey}
                                        hoveredKey={hoveredComponentKey}
                                        onComponentClick={handleComponentClick}
                                        onMouseEnter={handleComponentMouseEnter}
                                        onMouseLeave={handleComponentMouseLeave}
                                    />

                                    <ExpandableComponentSection
                                        title="Dörrar"
                                        groups={[{ components: doors, defaultImagePath: "placeholder.png" }]}
                                        building={currentBuilding}
                                        selectedComponentKey={selectedComponentKey}
                                        hoveredKey={hoveredComponentKey}
                                        onComponentClick={handleComponentClick}
                                        onMouseEnter={handleComponentMouseEnter}
                                        onMouseLeave={handleComponentMouseLeave}
                                    />

                                    <ExpandableComponentSection
                                        title="Ytskikt"
                                        groups={[
                                            { components: ceilingComponents, defaultImagePath: "placeholder.png" },
                                            { components: wallComponents, defaultImagePath: "placeholder.png" },
                                            { components: floorComponents, defaultImagePath: "placeholder.png" },
                                        ]}
                                        building={currentBuilding}
                                        selectedComponentKey={selectedComponentKey}
                                        hoveredKey={hoveredComponentKey}
                                        onComponentClick={handleComponentClick}
                                        onMouseEnter={handleComponentMouseEnter}
                                        onMouseLeave={handleComponentMouseLeave}
                                    />
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* Right Pane - Component details */}
                    <div style={{ flex: '1', minWidth: '300px', maxWidth: '700px' }}>
                        <RoomComponentDetailsPane
                            content={rightPaneContent}
                            building={currentBuilding}
                            buildingKey={buildingKey}
                            hasComponents={hasAnyComponents}
                        />
                    </div>
                </div>
            </div>

            <RoomFloorAreaModal
                show={showFloorAreaModal}
                onHide={() => setShowFloorAreaModal(false)}
                floors={floorAreaRows}
                currentTotal={totalFloorArea}
                verificationState={floorAreaState}
                onSave={handleSaveFloorArea}
            />
        </>
    );
};

export default RoomPage;
